import { snapToFrame } from '@src/utility/timecode';
import { useCallback, useEffect, useRef, useState } from 'react';

import type React from 'react';

/**
 * Drag gesture for a comment marker on the clip-preview scrubber (PLAT-10951).
 *
 * Only recognises the gesture and converts cursor x → time; what happens to the
 * playhead, the store, and the rendered dot is the caller's business. That
 * split keeps this unit-testable and lets `ClipCommentMarkers` stay presentation
 * plus delegation.
 *
 * Assembled from patterns already in the codebase rather than a new dependency:
 * the rect math mirrors `useScrubberDrag.computeTimeFromClientX`, the window-
 * level listeners + unmount strip come from the same hook, and the
 * distance threshold that separates a click from a drag comes from
 * `VolumeKeyPoints`. Two deliberate departures from `useScrubberDrag`:
 *
 * - **Live state is mirrored through refs.** `useScrubberDrag`'s `onMove`
 *   closes over the render-time `isSeeking`, which is always `false` on the
 *   render that attached it. Listeners here read refs, so they never go stale.
 * - **Escape cancels.** No other drag in this codebase supports it, but a
 *   marker drag mutates persisted data on drop, so an escape hatch matters.
 *
 * A gesture that never travels `DRAG_THRESHOLD_PX` is reported through
 * `onClick` instead of `onCommit` — the dot's original click-to-seek behaviour
 * has to survive. The marker must therefore NOT carry its own `onClick`: the
 * browser fires a native click after our `mouseup`, which would double-handle.
 */

/**
 * Cursor travel (px) before a press becomes a drag. Matches the threshold
 * `VolumeKeyPoints` uses to tell a keypoint click from a keypoint move.
 */
const DRAG_THRESHOLD_PX = 4;

export type CommentMarkerDragTarget = {
  id: string;
  /** The marker's committed time, restored if the drag is cancelled. */
  time: number;
};

export type CommentMarkerDragState = {
  id: string;
  /** Live, frame-snapped, clamped time under the cursor. */
  time: number;
};

export interface UseCommentMarkerDragOptions {
  /** The row the markers are positioned in; supplies the x → time rect. */
  trackRef: React.RefObject<HTMLElement | null>;
  duration: number;
  framerate: number;
  /** Fires on every drag frame so the caller can scrub the preview. */
  onPreview?: (time: number) => void;
  /** Drag released past the threshold — persist the new time. */
  onCommit: (id: string, time: number) => void;
  /** Released without travelling; the press was a click. */
  onClick?: (id: string, time: number) => void;
  /** Escape during a drag. The caller restores whatever it changed. */
  onCancel?: (target: CommentMarkerDragTarget) => void;
}

export interface UseCommentMarkerDrag {
  /** The in-flight drag, or `null`. Drives the ghost dot and its label. */
  drag: CommentMarkerDragState | null;
  /** Bind to each draggable marker's `onMouseDown`. */
  startDrag: (
    e: React.MouseEvent<HTMLElement>,
    target: CommentMarkerDragTarget,
  ) => void;
}

export function useCommentMarkerDrag({
  trackRef,
  duration,
  framerate,
  onPreview,
  onCommit,
  onClick,
  onCancel,
}: UseCommentMarkerDragOptions): UseCommentMarkerDrag {
  const [drag, setDrag] = useState<CommentMarkerDragState | null>(null);

  // Everything the window/document listeners read lives in refs — they are
  // attached once per gesture and must not see a render-time snapshot.
  const gestureRef = useRef<{
    target: CommentMarkerDragTarget;
    startX: number;
    startY: number;
    /** Flips once the cursor travels far enough to count as a drag. */
    moved: boolean;
    /** Latest computed time, committed on release. */
    time: number;
    detach: () => void;
  } | null>(null);
  const optionsRef = useRef({
    duration,
    framerate,
    onPreview,
    onCommit,
    onClick,
    onCancel,
  });
  optionsRef.current = {
    duration,
    framerate,
    onPreview,
    onCommit,
    onClick,
    onCancel,
  };

  const computeTimeFromClientX = useCallback(
    (clientX: number): number | null => {
      const el = trackRef.current;
      const { duration: dur, framerate: fps } = optionsRef.current;
      if (!el || dur <= 0) return null;
      const r = el.getBoundingClientRect();
      if (r.width <= 0) return null;
      const pct = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
      return Math.min(dur, snapToFrame(pct * dur, fps));
    },
    [trackRef],
  );

  // Strip listeners if the bar unmounts (fullscreen toggle, modal close)
  // mid-gesture, so nothing keeps firing against a dead component.
  useEffect(
    () => () => {
      gestureRef.current?.detach();
      gestureRef.current = null;
    },
    [],
  );

  const startDrag = useCallback(
    (e: React.MouseEvent<HTMLElement>, target: CommentMarkerDragTarget) => {
      if (optionsRef.current.duration <= 0) return;
      // Suppress the text selection a press-and-sweep would otherwise start.
      e.preventDefault();

      const onMove = (ev: MouseEvent) => {
        const gesture = gestureRef.current;
        if (!gesture) return;
        if (!gesture.moved) {
          const travelled = Math.hypot(
            ev.clientX - gesture.startX,
            ev.clientY - gesture.startY,
          );
          if (travelled < DRAG_THRESHOLD_PX) return;
          gesture.moved = true;
        }
        const next = computeTimeFromClientX(ev.clientX);
        if (next == null) return;
        gesture.time = next;
        setDrag({ id: gesture.target.id, time: next });
        optionsRef.current.onPreview?.(next);
      };

      const onUp = () => {
        const gesture = gestureRef.current;
        gesture?.detach();
        gestureRef.current = null;
        setDrag(null);
        if (!gesture) return;
        if (!gesture.moved) {
          optionsRef.current.onClick?.(gesture.target.id, gesture.target.time);
          return;
        }
        // A drag that lands back on the original frame is not a move. Compare
        // at frame resolution: the live time is snapped, while the stored one
        // need not be (a comment pinned to the raw playhead, or one typed as a
        // bare seconds value), and a sub-frame difference would otherwise
        // commit a write the user can't see.
        const original = snapToFrame(
          gesture.target.time,
          optionsRef.current.framerate,
        );
        if (gesture.time !== original) {
          optionsRef.current.onCommit(gesture.target.id, gesture.time);
        }
      };

      const onKeyDown = (ev: KeyboardEvent) => {
        if (ev.key !== 'Escape') return;
        const gesture = gestureRef.current;
        if (!gesture) return;
        // Escape resolves the innermost transient thing — this drag — and
        // nothing else: stop it reaching the modal's close handler or the
        // fullscreen-exit handler further down the propagation path.
        ev.preventDefault();
        ev.stopPropagation();
        gesture.detach();
        gestureRef.current = null;
        setDrag(null);
        optionsRef.current.onCancel?.(gesture.target);
      };

      const detach = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('keydown', onKeyDown, true);
      };

      gestureRef.current = {
        target,
        startX: e.clientX,
        startY: e.clientY,
        moved: false,
        time: target.time,
        detach,
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      // `window` + capture, not `document`: capture runs window → document, so
      // this beats every Escape handler on document regardless of when they
      // registered. Registering on document capture instead would lose to the
      // clip preview's fullscreen-exit handler, which is on document capture,
      // registered first (you're already in fullscreen when a drag starts), and
      // calls stopImmediatePropagation — so Escape would exit fullscreen and
      // abandon the drag without restoring the preview.
      window.addEventListener('keydown', onKeyDown, true);
    },
    [computeTimeFromClientX],
  );

  return { drag, startDrag };
}
